import { Button, Card, Modal, useToastContext } from '@payaca/ambrosia';
import { useGoogleLogin } from '@react-oauth/google';
import { FC, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { actions as usersActions } from '@/api/users';

import {
  ConnectionControl,
  ConnectionControlBenefitList,
} from '@/ui/components';

import GmailLogo from './gmail.png';
import GoogleButton from './google-button.png';

import { marketingSiteUrl } from '@/helpers/urlHelper';

import { parseHtmlToReact } from '@payaca/utilities/htmlUtilities';
import { useImportEmailServiceSignature } from '../../../../api/mutations/user/useImportEmailSignature';
import { useRemoveEmailSignature } from '../../../../api/mutations/user/useRemoveEmailSignature';
import useGetMe from '../../../../api/queries/me/useGetMe';

const GmailConnectionControl: FC = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const { data: me, refetch: refetchMe } = useGetMe();
  const { pushError } = useToastContext();

  const [showConfirmConnectModal, setShowConfirmConnectModal] = useState(false);
  const [showViewSignatureModal, setShowViewSignatureModal] = useState(false);

  const {
    mutateAsync: importEmailServiceSignatureMutation,
    isPending: isImportingEmailServiceSignature,
  } = useImportEmailServiceSignature();
  const {
    mutateAsync: removeEmailSignatureMutation,
    isPending: isRemovingEmailSignature,
  } = useRemoveEmailSignature();

  const userIntegrations = useMemo(() => me?.integrations, [me]);

  const connectToGoogle = useGoogleLogin({
    flow: 'auth-code',
    scope:
      'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly', // scope to access gmail - read/send
    // oxlint-disable-next-line typescript/no-misused-promises -- FIXME: pre-existing error: Promise-returning function provided to property where a void return was expected.
    onSuccess: async (codeResponse) => {
      setShowConfirmConnectModal(false);
      dispatch(
        usersActions.connectWithGoogle(codeResponse.code, (error: string) => {
          // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
          refetchMe();
          if (!error) {
            console.log('Connected with Google');
          } else {
            console.log('Unable to connect with Google');
            pushError(
              'There was an error connecting to your Google account. Please try again.'
            );
          }
        })
      );
    },
  });

  let connectionBody: JSX.Element;
  if (!userIntegrations?.google) {
    connectionBody = (
      <div>
        <p>Use your Gmail account in Payaca with our seamless integration.</p>
        <ConnectionControlBenefitList
          benefits={[
            'Send emails on a project from your Gmail account.',
            'Replies are automatically tracked on a project.',
          ]}
        />
      </div>
    );
  } else if (!userIntegrations.google?.isActive) {
    connectionBody = (
      <p>
        Your connection with your{' '}
        <span className="font-bold">{userIntegrations.google.email}</span>{' '}
        Google account has expired, please click the button below to reconnect.
      </p>
    );
  } else {
    connectionBody = (
      <div>
        <p>
          Your Gmail account{' '}
          <span className="font-bold">{userIntegrations.google.email}</span> has
          successfully been connected to Payaca.
        </p>
        {me?.emailSignature ? (
          <>
            <p>
              You email signature has been imported and will be used in outgoing
              emails.{' '}
              <Button
                onClick={() => setShowViewSignatureModal(true)}
                variant="link"
              >
                View signature
              </Button>
            </p>
          </>
        ) : (
          <Button
            onClick={async () => {
              await importEmailServiceSignatureMutation();
              // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
              refetchMe();
            }}
            isProcessing={isImportingEmailServiceSignature}
            variant="link"
          >
            Import signature
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <ConnectionControl
        disableConnection={false}
        connectionName="Gmail"
        isConnected={
          userIntegrations?.google ? userIntegrations.google?.isActive : false
        }
        allowConnectDisconnect
        onAddConnection={() => setShowConfirmConnectModal(true)}
        onRemoveConnection={(onConnectionRemoved: () => void) => {
          dispatch(
            usersActions.removeGoogleConnection(() => {
              // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
              refetchMe();
              onConnectionRemoved();
            })
          );
        }}
        logo={GmailLogo}
        hasConnectionExpired={
          userIntegrations?.google ? !userIntegrations.google?.isActive : false
        }
        disconnectionWarningMessage={
          <ul>
            <li>
              You will no longer be able to send emails via your Gmail account
              in Payaca.
            </li>
            <li>
              You will no longer be able to see newly received emails about your
              projects in Payaca.
            </li>
          </ul>
        }
      >
        {connectionBody}
      </ConnectionControl>
      <Modal
        title="Connect to Gmail"
        isOpen={showConfirmConnectModal}
        onClose={() => setShowConfirmConnectModal(false)}
      >
        <Modal.Body>
          <p>
            By connecting Payaca with your Google account to integrate with
            Gmail, Payaca will have access to the following:
          </p>
          <ul>
            <li>Send emails on behalf of your Gmail account</li>
            <li>Track email replies from your Gmail account</li>
          </ul>
          <p>
            Once connected, if you would like to stop using this integration,
            return to your profile page &gt; Connections and click
            &quot;Disconnect from Gmail&quot;.
          </p>
          <br />
          <p>
            For more information on the Google integration and the types of data
            we can access, please see our{' '}
            <a
              href={marketingSiteUrl('/privacy-policy')}
              target="_blank"
              rel="noreferrer"
            >
              Privacy policy
            </a>
            .
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Modal.Footer.Actions>
            <button
              className="bg-transparent hover:cursor-pointer"
              onClick={connectToGoogle}
            >
              <img src={GoogleButton} />
            </button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
      <Modal
        title="Email signature"
        isOpen={showViewSignatureModal}
        onClose={() => setShowViewSignatureModal(false)}
        size="lg"
      >
        <Modal.Body>
          <Card sizeVariant="lg">
            <Card.Body>
              {me?.emailSignature ? (
                // Ambrosia's prose styling forces `img { width: 100% }`, which
                // beats the width/height Gmail writes into a signature and
                // upscales small logos to the card width. `not-prose` opts the
                // preview out of it. Tailwind's preflight still applies
                // `img { max-width: 100% }`, so oversized logos stay contained
                // without needing a cap here.
                <div className="not-prose">
                  {parseHtmlToReact(me.emailSignature)}
                </div>
              ) : (
                <p>You do not have a signature set.</p>
              )}
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              onClick={async () => {
                await removeEmailSignatureMutation();
                setShowViewSignatureModal(false);
                // oxlint-disable-next-line typescript/no-floating-promises -- FIXME: pre-existing error: Promises must be awaited.
                refetchMe();
              }}
              isProcessing={isRemovingEmailSignature}
              colour="negative"
              variant="secondary"
            >
              Remove signature
            </Button>
            <Button onClick={() => setShowViewSignatureModal(false)}>
              Close
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GmailConnectionControl;
